import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import { Header } from "build-ts/metadata";
import showToast from "hooks/useCustomToast";

/**
 * Get List of Metadata Headers
 *
 * @param {MetadataTypeEnum} metadataType - metadata type to fetch the headers.
 * @param {number} pageSize - page size to fetch the headers.
 * @param {number} pageNumber - page number to fetch the headers.
 * @param {string} sortByField - field to sort the headers.
 * @param {boolean} sortAscending - sort ascending or descending.
 * @param {string} searchString - search string to filter the headers.
 * @param {string} authorId - author id to filter the headers.
 * @param {string[]} ownerIds - owner ids to filter the headers.
 * @returns {Promise<Header[]>} A promise that resolves to the list of headers.
 * @throws {Error} Throws an error if the request fails.
 */
export const fetchMetadataHeaders = async (
	metadataType: MetadataTypeEnum,
	pageSize: number = 20,
	pageNumber: number = 1,
	sortByField: string = "LAST_MODIFIED",
	sortAscending: boolean = false,
	searchString: string = "",
	authorId: string = "",
	ownerIds: string[] = [],
) => {
	try {
		const queryParams = new URLSearchParams({
			metadataType: metadataTypeEnumToJSON(metadataType),
			pageSize: String(pageSize),
			pageNumber: String(pageNumber),
			sortByField,
			sortAscending: String(sortAscending),
			searchString,
			authorId,
			ownerIds: JSON.stringify(ownerIds),
		});

		const response = await fetch(`${process.env.REACT_APP_GET_METADATA_HEADERS}?${queryParams}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
		});

		if (!response.ok) {
			showToast("Failed to fetch metadata headers", "", "error");
			return null;
		}
		const data = await response.json();
		return { headers: data.headers.map((header: Header[]) => Header.fromJSON(header)), totalRows: data.total_rows };
	} catch (error: any) {
		showToast(
			"Error fetching metadata headers",
			error?.message || "Unknown error occurred",
			"error"
		);
		return null;
	}
};

/**
 * Updates the name and description in the header of given metadata.
 * 
 * @param id - The ID of the metadata object
 * @param name - The new name for the metadata
 * @param description - The new description for the metadata
 * @param type - The type of metadata (e.g., LOGICAL_COLUMN, METRIC, ANALYST, etc.)
 * @returns The updated metadata header or null if the update fails
 * @throws {Error} Throws an error if the request fails
 */
export const updateHeader = async (
	id: string,
	name: string,
	description: string,
	type: MetadataTypeEnum
): Promise<Header | null> => {
	try {
		const queryParams = new URLSearchParams({
			id: id,
			type: metadataTypeEnumToJSON(type),
			name: name,
			description: description,
		});

		const response = await fetch(
			`${process.env.REACT_APP_UPDATE_METADATA_HEADER}?${queryParams}`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
			}
		);

		if (!response.ok) {
			showToast("Failed to update metadata header", "", "error");
			return null;
		}

		const data = await response.json();
		showToast("Metadata header updated successfully", "", "success");
		return Header.fromJSON(data);
	} catch (error: any) {
		showToast(
			"Error updating metadata header",
			error?.message || "Unknown error occurred",
			"error"
		);
		return null;
	}
};


